import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Box, Grid, Card, CardContent, CardMedia, Typography, CircularProgress, CardActionArea, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faMapMarkerAlt, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import { getWorkspaceEvents } from '../../../controllers/customer/workspaces/WorkspacesController';
import { GetEventByID } from '../../../controllers/customer/EventosController';

// IMPORTANTE: Asegúrate de importar tu POS
import EventBoxOfficePOS from '../../company/events/event/EventBoxOfficePOS'; 
import { GetEvent } from '../../../controllers/company/events/MerchantEventController';

export default function WorkspaceEvents() {
    // Atrapamos ambos parámetros. 'eventId' existirá solo si entramos a la taquilla
    const { uuid, eventId } = useParams(); 
    const history = useHistory();
    
    // Estados de la lista de eventos
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    // Estados de la taquilla
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [loadingEventDetails, setLoadingEventDetails] = useState(false);

    // 1. Cargar la lista de eventos Ligeros
    useEffect(() => {
        // Aprovechamos de setear el merchant para que el POS sepa en qué empresa estamos
        localStorage.setItem('work_mode_merchant_uuid', uuid);

        const loadEvents = async () => {
            const response = await getWorkspaceEvents(uuid);
            if (response && response.success) {
                setEvents(response.data.events);
            }
            setLoading(false);
        };
        loadEvents();
    }, [uuid]);

    // 2. MAGIA DE RUTAS: Escuchamos si la URL trae un ID de evento
    useEffect(() => {
        if (eventId) {
            fetchHeavyEvent(eventId);
        } else {
            // Si no hay eventId en la URL, limpiamos la taquilla para mostrar la lista
            setSelectedEvent(null);
        }
    }, [eventId]);

    // 3. Función para traer el evento pesado
    const fetchHeavyEvent = async (idToFetch) => {
        setLoadingEventDetails(true);
        try {
            let response = await GetEvent(idToFetch);
            
            if (response && response.success) {
                setSelectedEvent(response.data.event); 
            } else {
                alert("No se pudieron cargar los detalles del evento.");
                history.push(`/workspaces/${uuid}/events`); // Lo devolvemos a la lista si hay error
            }
        } catch (error) {
            console.error("Error al cargar evento completo:", error);
        } finally {
            setLoadingEventDetails(false);
        }
    };

    // 4. Acción al hacer clic en la tarjeta
    const handleSelectEvent = (eventSummary) => {
        // En vez de cargar el estado directo, cambiamos la URL.
        // Esto dispara el useEffect de arriba, manteniendo todo sincronizado.
        history.push(`/workspaces/${uuid}/events/${eventSummary.id}`);
    };

    // --- RENDERIZADO DE CARGA GLOBAL ---
    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
        </Box>
    );

    // --- RENDERIZADO DEL POS (TAQUILLA) ---
    if (eventId || loadingEventDetails || selectedEvent) {
        console.log('EL EVENTO QUE USAREMOS ES ===> ',selectedEvent);
        return (
            <Box height="100vh" display="flex" flexDirection="column">
                {/* Barra superior de la taquilla con botón de retroceso */}
                <Box p={2} bgcolor="#111827" color="white" display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">
                        {loadingEventDetails ? 'Cargando taquilla...' : `Taquilla: ${selectedEvent?.nombre}`}
                    </Typography>
                    <Button 
                        color="inherit" 
                        startIcon={<FontAwesomeIcon icon={faArrowLeft} />} 
                        onClick={() => history.push(`/workspaces/${uuid}/events`)}
                        sx={{ textTransform: 'none' }}
                    >
                        Volver a Eventos
                    </Button>
                </Box>
                
                {/* Contenido dinámico */}
                <Box flexGrow={1} p={2} overflow="auto">
                    {loadingEventDetails ? (
                        <Box display="flex" justifyContent="center" mt={10}>
                            <CircularProgress size={60} />
                        </Box>
                    ) : selectedEvent ? (
                        <EventBoxOfficePOS key={selectedEvent.id_evento} event={selectedEvent} />
                    ) : null}
                </Box>
            </Box>
        );
    }

    // --- RENDERIZADO DE LA LISTA DE EVENTOS ---
    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={1}>
                {/* Botón para regresar a los módulos (WorkspaceDashboard) */}
                <Button 
                    startIcon={<FontAwesomeIcon icon={faArrowLeft} />} 
                    onClick={() => history.push(`/workspaces/${uuid}`)}
                    sx={{ mr: 2, color: 'text.secondary' }}
                >
                    Atrás
                </Button>
                <Typography variant="h5" fontWeight="bold">
                    Eventos para Venta
                </Typography>
            </Box>
            
            <Grid container spacing={3} sx={{ mt: 1 }}>
                {events.map((event) => (
                    <Grid item xs={12} sm={6} md={4} key={event.id}>
                        <Card sx={{ borderRadius: '12px', border: '1px solid #eee' }}>
                            <CardActionArea onClick={() => handleSelectEvent(event)}>
                                <CardMedia
                                    component="img"
                                    height="160"
                                    image={event.image_url || event.image_path} // Soporte por si no está procesada
                                    alt={event.name}
                                />
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                        {event.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
                                        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '8px', fontSize: '14px' }} />
                                        <Typography variant="caption">{event.date} - {event.time}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, color: 'text.secondary' }}>
                                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '8px', fontSize: '14px' }} />
                                        <Typography variant="caption">{event.city}, {event.country_code}</Typography>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}